import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { notEmpty } from "@orb-labs/types";
import { AuthModal, type AuthModalInitialView } from "@/components/AuthModal";

interface LoginActionsContextType {
  handleLogin: () => Promise<void>;
  connectExternalWallet: () => Promise<void>;
}

const LoginActionsContext = createContext<LoginActionsContextType | undefined>(undefined);

export function useLoginActions(): LoginActionsContextType {
  const context = useContext(LoginActionsContext);
  if (!notEmpty(context)) {
    throw new Error("useLoginActions must be used within a LoginActionsProvider");
  }
  return context;
}

export function LoginActionsProvider({ children }: { children: React.ReactNode }) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState<AuthModalInitialView>("options");

  const handleLogin = useCallback(async () => {
    setAuthModalView("options");
    setShowAuthModal(true);
  }, []);

  const connectExternalWallet = useCallback(async () => {
    setAuthModalView("walletPicker");
    setShowAuthModal(true);
  }, []);

  const value = useMemo(() => ({ handleLogin, connectExternalWallet }), [handleLogin, connectExternalWallet]);

  return (
    <LoginActionsContext.Provider value={value}>
      {children}
      <AuthModal visible={showAuthModal} onClose={() => setShowAuthModal(false)} initialView={authModalView} />
    </LoginActionsContext.Provider>
  );
}
